import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useStore } from '../store/useStore';
import { usePantryStore } from '../lib/store';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import TextureGuideModal from '../components/TextureGuideModal';
import WHOGuideModal from '../components/WHOGuideModal';
import OnboardingTour from '../components/OnboardingTour';
import { 
  Camera, 
  Users, 
  Calendar, 
  Search, 
  Info, 
  ChefHat,
  ChevronRight,
  ShoppingCart,
  Flame,
  Sparkles,
  ArrowRight,
  Baby,
  Utensils,
  Wallet,
  Package,
  Palette,
  Database,
  Home,
  User,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { differenceInMonths, parseISO, format } from 'date-fns';

const modes = [
  {
    id: 'vision',
    title: 'Apa yang Ada di Kulkas?',
    description: 'Foto bahan makananmu, AI akan carikan resep yang pas.',
    icon: Camera,
    color: 'bg-blue-100 text-blue-600',
    tag: 'Populer'
  },
  {
    id: 'twin-cook',
    title: 'Twin Cook: Masak Bareng',
    description: 'Masak 1 menu untuk Bapak-Ibu & MPASI Adek sekaligus.',
    icon: Users,
    color: 'bg-amber-100 text-amber-600',
    tag: 'Hemat Waktu'
  },
  {
    id: 'planner',
    title: 'Rencana Menu Mingguan',
    description: 'Atur jadwal makan keluarga agar tidak bingung setiap hari.',
    icon: Calendar,
    color: 'bg-green-100 text-green-600',
  },
  {
    id: 'search',
    title: 'Cari Resep Spesifik',
    description: 'Explorasi ribuan resep MPASI bergizi dari IDAI & WHO.',
    icon: Search,
    color: 'bg-purple-100 text-purple-600',
  },
  {
    id: 'guide',
    title: 'Panduan Tekstur & Nutrisi',
    description: 'Cek tekstur yang tepat sesuai usia si Kecil.',
    icon: Info,
    color: 'bg-stone-100 text-stone-600',
  }
];

export default function HomePage() {
  const navigate = useNavigate();
  const [isGuideOpen, setIsGuideOpen] = React.useState(false);
  const [isWHOGuideOpen, setIsWHOGuideOpen] = React.useState(false);
  const [isTargetModalOpen, setIsTargetModalOpen] = React.useState(false);
  const [targetSelection, setTargetSelection] = React.useState<'twin'|'baby'|'family'>('twin');
  const [strictPantry, setStrictPantry] = React.useState(false);
  const { babyProfile, skipBabyMode, savedShoppingPlan, savedBudgetPlan } = useStore();
  const { items } = usePantryStore();
  
  const babyAgeMonths = babyProfile?.dob && !skipBabyMode
    ? differenceInMonths(new Date(), parseISO(babyProfile.dob)) 
    : 0;

  return (
    <div className="flex min-h-screen flex-col bg-stone-50 font-sans">
      {/* Header Section */}
      <header className="pt-10 md:pt-14 px-6 md:px-10 lg:px-16 xl:px-20 pb-6 bg-white border-b border-stone-100">
        <div className="flex justify-between items-center mb-8 md:mb-11 max-w-md md:max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
          <div>
            <p className="text-[10.5px] font-bold text-stone-500 uppercase tracking-[0.2em] mb-1">
              {(skipBabyMode && (!babyProfile?.caregiverName || babyProfile?.caregiverName?.toLowerCase() === 'anda' || babyProfile?.caregiverName === 'Mama')) ? 'Halo,' : `Halo, ${babyProfile?.caregiverName || 'Mama'}${!skipBabyMode && babyProfile?.name ? `-nya ${babyProfile.name}` : ''}`}
            </p>
            <h1 className="text-2xl font-bold text-stone-900 leading-tight">Mau masak apa?</h1>
          </div>
          <div className="flex items-center gap-1">
            <Button id="tour-pantry-btn" variant="ghost" size="icon" className="hover:bg-stone-100 rounded-full" onClick={() => navigate('/pantry')}>
              <div className="relative">
                <Package className="h-6 w-6 text-stone-600" />
                {items.filter(i => i.status !== 'available').length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white" />
                )}
              </div>
            </Button>
            <button
              onClick={() => navigate('/profile')}
              className="w-10 h-10 ml-2 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center overflow-hidden hover:bg-amber-200 active:scale-95 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500"
              title="Edit Profil & Preferensi"
            >
              <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center text-white text-[10px] font-bold">
                {skipBabyMode ? (babyProfile?.caregiverName?.[0] || 'U') : (babyProfile?.name?.[0].toUpperCase() || 'A')}
              </div>
            </button>
          </div>
        </div>
        
        {/* Quick Info Widget - Sleek, Balanced & Elegant for Senior UI/UX Polish */}
        {!skipBabyMode && (
          <button 
            onClick={() => setIsWHOGuideOpen(true)}
            className="w-full text-left max-w-md md:max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto flex items-center justify-between gap-4 shadow-sm border border-amber-200/50 hover:border-amber-400/40 cursor-pointer bg-gradient-to-r from-amber-50/30 to-white text-stone-900 rounded-3xl py-3.5 px-6 md:py-5 md:px-8 hover:shadow-md hover:from-amber-50/45 hover:to-white transition-all active:scale-[0.98]"
          >
            <div className="flex-1 min-w-0">
              <span className="text-[10px] md:text-[11px] font-bold text-amber-700/95 uppercase tracking-wider block mb-0.5">
                Target Gizi {babyProfile?.name || 'Adek'} • {babyAgeMonths} bulan
              </span>
              <h3 className="text-sm md:text-base font-semibold text-stone-800 tracking-tight leading-snug">
                Menu Gizi Seimbang 4 Bintang
              </h3>
              <p className="hidden sm:block text-[11px] text-stone-500 font-medium mt-0.5">
                Selaras dengan standar rekomendasi gizi WHO & IDAI
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              <div className="flex gap-0.5 bg-amber-100/50 border border-amber-200/40 px-2 py-1 rounded-2xl">
                {[1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-500 fill-amber-500" />
                ))}
              </div>
              <ChevronRight className="h-4 w-4 md:h-5 md:w-5 text-amber-600/80" />
            </div>
          </button>
        )}
      </header>

      <main className="flex-1 w-full max-w-md md:max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto p-6 md:p-10 lg:p-16 xl:p-20 space-y-8 md:space-y-12 overflow-y-auto">
        {/* Onboarding walkthrough for first-time users */}
        <OnboardingTour itemsCount={items.length} />

        {/* Baby < 6 Months Warning */}
        {!skipBabyMode && babyAgeMonths < 6 && (
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-amber-50 border border-amber-200/60 rounded-3xl p-6 flex gap-4 items-start w-full"
          >
            <div className="p-2 bg-amber-100 rounded-xl text-amber-750">
              <Info className="h-5 w-5" />
            </div>
            <div className="space-y-1 flex-1">
              <h4 className="font-bold text-sm text-amber-900">Info Penting buat {babyProfile?.caregiverName || 'Mama'}!</h4>
              <p className="text-amber-800 text-[10.5px] leading-relaxed">
                Usia {babyProfile?.name || 'Adek'} baru {babyAgeMonths} bulan. IDAI & WHO menyarankan ASI eksklusif sampai 6 bulan sebagai batas minimal ya, {babyProfile?.caregiverName || 'Mama'}.
              </p>
              <p className="text-stone-600 text-[10px] leading-relaxed pt-1.5 border-t border-amber-200/50 mt-1.5">
                💡 <strong className="text-stone-800">Bisa Diakses Sepenuhnya:</strong> Seluruh fitur resep MPASI & Twin Cook tetap <strong className="text-amber-700">terbuka 100%</strong> untuk {babyProfile?.caregiverName || 'Mama'} gunakan! {babyProfile?.caregiverName || 'Mama'} bisa bebas mencari inspirasi menu, belajar tekstur gizi, serta mencicil rencana sehat untuk si Kecil dari sekarang.
              </p>
            </div>
          </motion.div>
        )}

        {/* Feature Groups (Masak Cepat & Rencana Masak) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 lg:gap-10 w-full">
          {/* Section: Masak Cepat */}
          <section className="space-y-3">
            <div className="flex justify-between items-end px-1">
              <h2 className="text-xl font-bold text-stone-900 leading-tight">Masak Cepat</h2>
              <p className="text-[9px] font-extrabold text-stone-500 uppercase tracking-widest">Siap eksekusi</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Mode 1: Foto Bahan (N1.06) */}
              <Button 
                id="tour-camera-btn"
                variant="outline" 
                onClick={() => navigate('/cook/foto')}
                className="h-[140px] flex-col items-center justify-center p-5 rounded-3xl border-2 border-stone-100 bg-white hover:border-amber-500 hover:bg-amber-50 transition-all group shadow-sm hover:shadow-md gap-3"
              >
                <div className="bg-amber-100 p-2.5 rounded-2xl group-hover:bg-amber-200 transition-colors">
                  <Camera className="h-6 w-6 text-amber-600" />
                </div>
                <div className="text-center space-y-1 mt-1">
                  <p className="text-sm font-bold text-stone-900 leading-tight">Foto Bahan</p>
                  <p className="text-[10px] text-stone-500 font-semibold leading-tight">Cek kulkas & meja</p>
                </div>
              </Button>

              {/* Mode 3: Darurat (N1.10 / N1.16) */}
              <Button 
                variant="outline" 
                onClick={() => navigate('/cook/darurat')}
                className="h-[140px] flex-col items-center justify-center p-5 rounded-3xl border-2 border-stone-100 bg-white hover:border-orange-500 hover:bg-orange-50 transition-all group shadow-sm hover:shadow-md gap-3"
              >
                <div className="bg-orange-100 p-2.5 rounded-2xl group-hover:bg-orange-200 transition-colors shadow-sm">
                  <Flame className="h-6 w-6 text-orange-600" />
                </div>
                <div className="text-center space-y-1 mt-1">
                  <p className="text-sm font-bold text-stone-900 leading-tight">Darurat Lapar</p>
                  <p className="text-[10px] text-stone-500 font-semibold leading-tight">&lt; 15 menit matang</p>
                </div>
              </Button>
            </div>
          </section>

          {/* Section: Rencana Masak */}
          <section className="space-y-3">
            <div className="flex justify-between items-end px-1">
              <h2 className="text-xl font-bold text-stone-900 leading-tight">Rencana Masak</h2>
              <p className="text-[9px] font-extrabold text-stone-500 uppercase tracking-widest">Rencana strategis</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Mode 2: Budget Mode (N1.09 / N1.15) */}
              <Button 
                variant="outline" 
                onClick={() => navigate('/cook/budget')}
                className={`h-[140px] flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all group shadow-sm hover:shadow-md gap-3 relative overflow-hidden ${
                  savedBudgetPlan 
                    ? 'border-emerald-300 bg-emerald-50/40 hover:border-emerald-500 hover:bg-emerald-50' 
                    : 'border-stone-100 bg-white hover:border-emerald-500 hover:bg-emerald-50'
                }`}
              >
                {savedBudgetPlan && (
                  <div className="absolute top-2 right-2 bg-emerald-600 text-[8px] text-white font-extrabold uppercase px-1.5 py-0.5 rounded-full animate-pulse">
                    Aktif ✨
                  </div>
                )}
                <div className={`p-2.5 rounded-2xl group-hover:bg-emerald-200 transition-colors shadow-sm ${
                  savedBudgetPlan ? 'bg-emerald-200' : 'bg-emerald-100'
                }`}>
                  <Wallet className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="text-center space-y-1 mt-1">
                  <p className="text-sm font-bold text-stone-900 leading-tight">Mode Hemat</p>
                  <p className="text-[10px] text-stone-500 font-semibold leading-tight">
                    {savedBudgetPlan ? 'Lihat Draf Aktif' : 'Gizi per Rupiah'}
                  </p>
                </div>
              </Button>

              {/* Mode 4: Plan Belanja (N1.08) */}
              <Button 
                variant="outline" 
                onClick={() => navigate('/cook/plan-belanja')}
                className={`h-[140px] flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all group shadow-sm hover:shadow-md gap-3 relative overflow-hidden ${
                  savedShoppingPlan 
                    ? 'border-purple-300 bg-purple-50/40 hover:border-purple-500 hover:bg-purple-50' 
                    : 'border-stone-100 bg-white hover:border-purple-500 hover:bg-purple-50'
                }`}
              >
                {savedShoppingPlan && (
                  <div className="absolute top-2 right-2 bg-purple-600 text-[8px] text-white font-extrabold uppercase px-1.5 py-0.5 rounded-full animate-pulse">
                    Aktif ✨
                  </div>
                )}
                <div className={`p-2.5 rounded-2xl group-hover:bg-purple-200 transition-colors shadow-sm ${
                  savedShoppingPlan ? 'bg-purple-200' : 'bg-purple-100'
                }`}>
                  <ShoppingCart className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text-center space-y-1 mt-1">
                  <p className="text-sm font-bold text-stone-900 leading-tight">Rencana Belanja</p>
                  <p className="text-[10px] text-stone-500 font-semibold leading-tight">
                    {savedShoppingPlan ? 'Lihat Draf Aktif' : 'Susun dari nol'}
                  </p>
                </div>
              </Button>
            </div>
          </section>
        </div>

        {/* Primary Menus (Twin Cook & Guide) */}
        <div className={`grid grid-cols-1 ${!skipBabyMode ? 'md:grid-cols-2' : ''} gap-4 md:gap-8 lg:gap-10 w-full`}>
          {/* Primary Feature: Twin Cook (N1.21) */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <h3 className="text-[10px] font-extrabold text-stone-500 uppercase tracking-widest">Menu Harian</h3>
            </div>
            <Button 
              onClick={() => {
                if (skipBabyMode) {
                  setTargetSelection('family');
                }
                setIsTargetModalOpen(true);
              }}
              className="w-full h-auto py-4 bg-stone-900 text-white rounded-3xl flex items-center justify-between px-4 sm:px-6 shadow-xl shadow-stone-900/10 active:scale-95 transition-all group relative overflow-hidden"
            >
              <div className="flex items-center gap-3 relative z-10 flex-1 min-w-0">
                <div className="bg-white/10 p-2 sm:p-3 rounded-2xl shrink-0">
                  <ChefHat className="h-6 w-6" />
                </div>
                <div className="text-left flex-1 min-w-0 pr-1">
                  <p className="text-sm sm:text-lg font-bold leading-tight flex-wrap">Ide Masak Harian</p>
                  <p className="text-[10px] sm:text-[11px] opacity-70 font-medium leading-snug whitespace-normal break-words mt-0.5">
                    {skipBabyMode ? 'Cari resep porsi besar keluarga' : 'Masak santai Twin Cook & MPASI 4⭐'}
                  </p>
                </div>
              </div>
              <div className="bg-amber-500 text-amber-950 text-[9px] font-bold px-2 py-1 rounded-full relative z-10 shrink-0 ml-1 shadow-sm border border-amber-400/50">HITS</div>
              <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/5 rounded-full pointer-events-none group-hover:scale-150 transition-transform duration-700" />
            </Button>
          </section>

          {/* List Action: Guide & Texture (N1.19 context) */}
          {!skipBabyMode && (
            <section className="space-y-3">
              <div className="hidden md:flex items-center gap-2 px-1">
                <Info className="h-4 w-4 text-amber-500" />
                <h3 className="text-[10px] font-extrabold text-stone-500 uppercase tracking-widest">Panduan Gizi</h3>
              </div>
              <Button 
                onClick={() => setIsGuideOpen(true)}
                className="w-full h-auto py-4 bg-white border border-stone-200 text-stone-900 rounded-3xl flex items-center justify-between px-4 sm:px-6 shadow-sm active:scale-95 transition-all hover:bg-amber-50 hover:border-amber-200"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="bg-amber-50 p-2 sm:p-3 rounded-2xl shrink-0 text-amber-600 border border-amber-100/50">
                     <Info className="h-6 w-6" />
                  </div>
                  <div className="text-left flex-1 min-w-0 pr-1">
                    <p className="text-sm sm:text-lg font-bold text-stone-900 leading-tight">Tekstur Adek</p>
                    <p className="text-[10px] sm:text-[11px] text-stone-500 font-medium leading-snug mt-0.5">Panduan WHO</p>
                  </div>
                </div>
              </Button>
            </section>
          )}
        </div>
      </main>

      {/* Bottom Nav Bar */}
      <nav className="h-22 bg-white border-t border-stone-100 sticky bottom-0 z-10 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)] flex items-center">
        <div className="w-full max-w-md md:max-w-lg lg:max-w-xl mx-auto flex items-center justify-around px-8 pb-4">
          <div className="flex flex-col items-center gap-1.5 text-amber-600">
            <div className="p-1 rounded-lg">
              <Home className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider">Utama</span>
          </div>
          <button 
            onClick={() => toast.info(`Fitur koleksi resep favorit ${babyProfile?.caregiverName || 'Mama'} akan hadir di pembaruan mendatang! Pantau terus ya, ${babyProfile?.caregiverName || 'Mama'}! ✨`)}
            className="flex flex-col items-center gap-1.5 text-stone-300 hover:text-stone-450 active:scale-95 transition-all cursor-pointer focus:outline-none"
          >
            <div className="p-1 rounded-lg">
              <ChefHat className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-medium uppercase tracking-wider">Resep</span>
          </button>
          <button 
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1.5 text-stone-400 hover:text-stone-600 active:scale-95 transition-all cursor-pointer focus:outline-none"
          >
            <div className="p-1 rounded-lg hover:bg-stone-50 transition-colors">
              <User className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-medium uppercase tracking-wider">Profil</span>
          </button>
        </div>
      </nav>

      <TextureGuideModal 
        isOpen={isGuideOpen} 
        onOpenChange={setIsGuideOpen} 
        ageInMonths={babyAgeMonths} 
      />
      <WHOGuideModal 
        isOpen={isWHOGuideOpen}
        onOpenChange={setIsWHOGuideOpen}
      />
      <AnimatePresence>
        {isTargetModalOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
              onClick={() => setIsTargetModalOpen(false)}
            />
            <motion.div 
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              className="relative w-full max-w-sm bg-white rounded-[2rem] p-6 shadow-2xl"
            >
              <h3 className="text-xl font-bold text-stone-900 mb-2">
                {skipBabyMode ? 'Pengaturan Menu Harian' : 'Untuk siapa AI memasak?'}
              </h3>
              <p className="text-sm text-stone-500 mb-6">
                {skipBabyMode ? 'Tentukan preferensi pencarian resep harian' : 'Pilih mode generasi resep'}
              </p>
              
              {!skipBabyMode && (
                <div className="space-y-3 mb-6">
                  <button
                    onClick={() => setTargetSelection('twin')}
                    className={`w-full flex items-center p-4 rounded-2xl border-2 text-left transition-colors ${
                      targetSelection === 'twin' ? 'border-amber-500 bg-amber-50' : 'border-stone-100 hover:border-stone-200'
                    }`}
                  >
                    <div className={`p-2 rounded-xl mr-4 ${targetSelection === 'twin' ? 'bg-amber-200 text-amber-700' : 'bg-stone-100 text-stone-600'}`}>
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-stone-900">Twin Cook (Bareng)</p>
                      <p className="text-xs text-stone-500 mt-0.5">1 Masakan untuk Adek & Dewasa</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setTargetSelection('baby')}
                    className={`w-full flex items-center p-4 rounded-2xl border-2 text-left transition-colors ${
                      targetSelection === 'baby' ? 'border-amber-500 bg-amber-50' : 'border-stone-100 hover:border-stone-200'
                    }`}
                  >
                    <div className={`p-2 rounded-xl mr-4 ${targetSelection === 'baby' ? 'bg-amber-200 text-amber-700' : 'bg-stone-100 text-stone-600'}`}>
                      <Baby className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-stone-900">Hanya MPASI</p>
                      <p className="text-xs text-stone-500 mt-0.5">Fokus nutrisi 4 bintang Adek</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setTargetSelection('family')}
                    className={`w-full flex items-center p-4 rounded-2xl border-2 text-left transition-colors ${
                      targetSelection === 'family' ? 'border-amber-500 bg-amber-50' : 'border-stone-100 hover:border-stone-200'
                    }`}
                  >
                    <div className={`p-2 rounded-xl mr-4 ${targetSelection === 'family' ? 'bg-amber-200 text-amber-700' : 'bg-stone-100 text-stone-600'}`}>
                      <Utensils className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-stone-900">Hanya Keluarga</p>
                      <p className="text-xs text-stone-500 mt-0.5">Menu sedap untuk orang tua</p>
                    </div>
                  </button>
                </div>
              )}

              {/* Strict Mode Toggle */}
              <div className="flex items-center justify-between p-3.5 bg-amber-50/50 rounded-2xl border border-amber-100 mb-6">
                <div className="flex items-start gap-2.5 cursor-pointer" onClick={() => setStrictPantry(!strictPantry)}>
                  <div className="mt-0.5">
                    <input 
                      type="checkbox" 
                      id="strictPantryToggle"
                      checked={strictPantry}
                      onChange={(e) => setStrictPantry(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 rounded text-amber-600 border-stone-300 focus:ring-amber-500"
                    />
                  </div>
                  <label htmlFor="strictPantryToggle" className="cursor-pointer select-none">
                    <p className="text-xs font-bold text-stone-800">Hanya Gunakan Stok Kulkas</p>
                    <p className="text-[10px] text-stone-500 mt-0.5 leading-snug">AI tidak akan menambah bahan belanja ekstra (misal: bayam jika tidak ada di kulkas).</p>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="ghost" className="flex-1" onClick={() => setIsTargetModalOpen(false)}>Batal</Button>
                <Button 
                  className="flex-1 bg-amber-600 hover:bg-amber-700 rounded-full font-bold"
                  onClick={() => {
                    setIsTargetModalOpen(false);
                    const strictSuffix = strictPantry ? '?strict=true' : '';
                    if (targetSelection === 'twin') {
                      navigate(`/cook/twin/active${strictSuffix}`);
                    } else if (targetSelection === 'baby') {
                      navigate(`/cook/mpasi/active${strictSuffix}`);
                    } else if (targetSelection === 'family') {
                      navigate(`/cook/family/active${strictSuffix}`);
                    }
                  }}
                >
                  Pilih
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
